import { isInviteCode } from 'constants/play';
import { safePlayReturnTo } from './discordAuth';

/**
 * Where a brand-new account goes after sign-up (Asana 1220000146037011,
 * Decision 5, Desired Behavior 2, Eric's rulings 31 and 32 of 2026-10-30).
 *
 * A Play sign-up (the backend tagged the account onboardingLevel PLAY, or the
 * stored returnTo points under /play) never sees the phone and address steps:
 * - an invite returnTo (/play/{code}) goes straight back there, where the
 *   invite name step (username, first and last name) joins the event;
 * - any other /play returnTo (the host flow, the landing) goes through the
 *   host name step at /play/auth/name, username only, then back.
 * Everything else is the full onboarding.
 */

export const PLAY_NAME_STEP_PATH = '/play/auth/name';

const PLAY_RETURN_RE = /^\/play(\/|\?|$)/;

export const isPlayReturnTo = (value: string | null | undefined): boolean => !!value && PLAY_RETURN_RE.test(value);

/** True for /play/{code} and anything under it; reserved words are never codes. */
export const isInviteReturnTo = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const match = value.match(/^\/play\/([^/?#]+)/);
  return !!match && isInviteCode(match[1]);
};

export const playNameStepPath = (returnTo: string | null | undefined): string =>
  `${PLAY_NAME_STEP_PATH}?returnTo=${encodeURIComponent(safePlayReturnTo(returnTo, '/play'))}`;

export type SignupNext = { kind: 'full' } | { kind: 'play'; to: string };

export const nextAfterSignup = (input: { onboardingLevel?: 'PLAY' | 'FULL' | null; returnTo: string | null | undefined }): SignupNext => {
  const play = input.onboardingLevel === 'PLAY' || isPlayReturnTo(input.returnTo);
  if (!play) return { kind: 'full' };
  const target = safePlayReturnTo(input.returnTo, '/play');
  if (isInviteReturnTo(target)) return { kind: 'play', to: target };
  return { kind: 'play', to: playNameStepPath(target) };
};
